import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  createTheme,
  responsiveFontSizes,
  StyledEngineProvider,
  ThemeProvider,
} from '@mui/material/styles';
import { Localization, ukUA, ruRU, enUS } from '@mui/material/locale';
import { createCtx } from '../utils/helpers';
import getTheme from '../theme/theme';

const muiThemeLocalizationMap: { [index: string]: Localization } = {
  uk: ukUA,
  ru: ruRU,
  en: enUS,
};

export type Languages = { value: string; dir: string; label: string }[];

const [useLanguagesContext, LanguagesContextProvider] = createCtx<Languages>();

export { useLanguagesContext };

const lngOptions: { [index: string]: { name: string } } = {
  uk: { name: 'languages.uk' },
  // ru: { name: 'languages.ru' },
  // en: { name: 'languages.en' },
};

export function LanguagesProvider({
  children,
}: React.PropsWithChildren<unknown>): JSX.Element {
  const { i18n, t } = useTranslation();

  const { dir, language } = i18n;
  const direction = dir(language);

  const [languages] = useState<Languages>(() =>
    Object.keys(lngOptions).map((lngKey) => ({
      value: lngKey,
      dir: i18n.dir(lngKey),
      label: t(lngOptions[lngKey].name),
    }))
  );

  const muiThemeLocale = useMemo(
    () => muiThemeLocalizationMap[language] || ukUA,
    [language]
  );

  const theme = useMemo(
    () => responsiveFontSizes(createTheme(getTheme({ direction }), muiThemeLocale)),
    [muiThemeLocale, direction]
  );

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={theme}>
        <LanguagesContextProvider value={languages}>{children}</LanguagesContextProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
}
